import { Text, VStack, HStack, Box, Tooltip } from '@chakra-ui/react';
import { useState } from 'react';
import { CrowdstikeUserHistoryDataSection } from '../CrowdstrikeModalSection/CrowdstikeUserHistoryDataSection';
import { CrowdStrikeMark } from '@gamma/ui/logos';
import { AzureADMark } from '../../../../../../../../../gamma/libs/gamma/logos/src/lib/AzureADMark';
import { UserPrivileges } from '@investigator/shared/providers';
import { OrganizedEDRData } from '@investigator/features/entities/types';
import { NoDataSection } from '../NoDataSection';
import { useEntitiesContext } from '@investigator/features/entities/context';
import { i18n } from '@investigator/shared/localization';
import { EntraIDUserHistoryDataSection } from '../EntraIDModalSection/EntraIDUserHistoryDataSection';
import * as Logos from '@gamma/logos';
import { useIdentityIntegrationSettings } from '@investigator/features/integrations/hooks';
const { integrations } = i18n.pages;
const { crowdStrike } = integrations.crowdStrike;
const { entra, signInHistory, enableCrowdStrike, enableEntraId } =
  integrations.entra;

export interface UserHistorySectionProps {
  tenantPrivileges: UserPrivileges | undefined;
  organizedData: OrganizedEDRData | null;
}

export const UserHistorySection = ({
  tenantPrivileges,
  organizedData,
}: UserHistorySectionProps): JSX.Element => {
  const { selectedEntity } = useEntitiesContext();
  const { tenantInfo } = selectedEntity || {};

  const { tenant_id = '' } = tenantInfo || {};
  const { identityIntegrationEnabled } = useIdentityIntegrationSettings();

  // Set initial toggle state based on tenant privileges
  const [isCrowdstikeUserHistory, setIsCrowdstikeUserHistory] = useState(() => {
    if (tenantPrivileges?.crowdstrike) {
      return true; // CrowdStrike enabled, default to it
    } else if (tenantPrivileges?.microsoft_entra_id) {
      return false; // CrowdStrike disabled, Entra enabled, default to Entra
    } else {
      return true; // Both disabled, fallback (won't really render anything)
    }
  });

  const renderCrowdstikeUserHistory = () => {
    if (tenantPrivileges?.crowdstrike && organizedData?.crowdstrike) {
      return <CrowdstikeUserHistoryDataSection />;
    } else {
      return <NoDataSection header={null} />;
    }
  };

  const renderEntraIDUserHistory = () => {
    // Entra ID signin history requires correlation data from Crowdstrike or Defender
    if (!organizedData?.crowdstrike && !organizedData?.microsoft_defender) {
      return <NoDataSection header={null} />;
    }
    // Ensure tenant_id is not null/undefined
    if (!tenant_id) {
      return <NoDataSection header={null} />;
    }
    return <EntraIDUserHistoryDataSection tenant={tenant_id} />;
  };

  return (
    // <VStack alignItems="start">
    <VStack w="100%" alignItems="start" spacing={3}>
      <VStack w="100%" alignItems="left" spacing={3}>
        <HStack>
          <Box>
            <Logos.UserIcon />
          </Box>
          <Text fontWeight="bold" fontSize="lg">
            {signInHistory}
          </Text>
        </HStack>

        {/* Toggle Switch for Crowdstrike/Entra ID */}
        <HStack spacing={4} alignItems="center">
          <Tooltip
            label={!tenantPrivileges?.crowdstrike ? enableCrowdStrike : ''}
            shouldWrapChildren
            placement="top"
          >
            <HStack
              spacing={2}
              px={3}
              py={1.5}
              borderRadius="20px"
              bg={isCrowdstikeUserHistory ? 'blue.500' : 'transparent'}
              cursor={tenantPrivileges?.crowdstrike ? 'pointer' : 'not-allowed'}
              opacity={tenantPrivileges?.crowdstrike ? 1 : 0.5}
              onClick={() => {
                if (tenantPrivileges?.crowdstrike)
                  setIsCrowdstikeUserHistory(true);
              }}
            >
              <CrowdStrikeMark height={'20px'} width={'20px'} />
              <Text
                fontSize="sm"
                fontWeight="medium"
                color={isCrowdstikeUserHistory ? '#fff' : 'gray.400'}
              >
                {crowdStrike}
              </Text>
            </HStack>
          </Tooltip>
          {identityIntegrationEnabled && (
            <Tooltip
              label={!tenantPrivileges?.microsoft_entra_id ? enableEntraId : ''}
              shouldWrapChildren
              placement="top"
            >
              <HStack
                spacing={2}
                px={3}
                py={1.5}
                borderRadius="20px"
                bg={!isCrowdstikeUserHistory ? 'blue.500' : 'transparent'}
                cursor={
                  tenantPrivileges?.microsoft_entra_id
                    ? 'pointer'
                    : 'not-allowed'
                }
                opacity={tenantPrivileges?.microsoft_entra_id ? 1 : 0.5}
                onClick={() => {
                  if (tenantPrivileges?.microsoft_entra_id)
                    setIsCrowdstikeUserHistory(false);
                }}
              >
                <Box
                  width="20px"
                  height="20px"
                  display="flex"
                  alignItems="center"
                >
                  <AzureADMark boxSize={6} color="currentColor" />
                </Box>
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  color={!isCrowdstikeUserHistory ? '#fff' : 'gray.400'}
                >
                  {entra}
                </Text>
              </HStack>
            </Tooltip>
          )}
        </HStack>
      </VStack>
      {isCrowdstikeUserHistory
        ? renderCrowdstikeUserHistory()
        : renderEntraIDUserHistory()}
    </VStack>
  );
};
